/**
 * TopicTimeline — small timeline strip for the TopicDetailPanel.
 *
 * Plots transactions as paired vertical bars per date bin: the sum of
 * positive amounts rises above the midline (green) and the sum of
 * negative amounts drops below (red). Income and expenses on the same
 * day are shown side-by-side rather than netted, so a $300 deposit
 * does not visually cancel an $80 spend. Key dates render as labeled
 * vertical markers (point-kind) or translucent bands (span-kind) on
 * the same horizontal axis. Pure presentation — no fetching.
 *
 * Rendering strategy: SVG with `preserveAspectRatio="none"` for the
 * stretchable bits (bands, key-date marker lines, midline) plus an
 * absolutely-positioned DOM overlay for elements that must NOT stretch
 * (transaction bars, X-axis ticks, sparse-mode inline labels). SVG
 * shapes with fixed proportions can't survive non-uniform aspect-ratio
 * stretch — they distort, which was the original "smudge" bug.
 *
 * Props:
 *   transaction_summaries: Array<{ id, date, amount }>
 *   key_dates: Array<{ dateID, label, start_date, end_date, kind, confidence }>
 *   onSelectDate?: (date_id) => void
 */

import React, { useMemo } from 'react';
import { formatCurrency } from '../../utils/formatters';
import styles from './TopicTimeline.module.css';

const CONFIDENCE_COLOR_VAR = {
  confirmed: 'var(--color-success-600)',
  estimated: 'var(--color-warning-600)',
  asserted: 'var(--color-text-secondary, var(--color-navy-700))',
};

// Plot geometry. Up bar zone = midY - PADDING_TOP, down bar zone =
// HEIGHT - midY - PADDING_BOTTOM. Both must comfortably exceed MAX_BAR_PX
// or down bars collide with the tick-label row at the bottom.
const HEIGHT = 124;
const PADDING_TOP = 28;
const PADDING_BOTTOM = 24;

// Key-date label sizing, mirrors NetWorthChart. Labels render above the
// plot rotated -45° so closely-spaced markers don't overlap; the lane
// height is sized from the longest label's vertical projection so the
// rotated text isn't clipped at the wrapper edge.
const KEY_DATE_FONT_PX = 8;
const KEY_DATE_LANE_MIN_PX = 40;

// Threshold below which we render inline date+amount labels under each dot
// instead of relying on hover tooltips alone. Sparse topics (1-5 transactions)
// gave the worst first impression in field testing — a single-dot timeline
// without inline labels reads as empty.
const SPARSE_DOT_THRESHOLD = 5;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

function parseDate(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatDateLabel(d) {
  if (!d) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatTickLabel(d, granularity) {
  if (!d) return '';
  if (granularity === 'year') return String(d.getFullYear());
  if (granularity === 'quarter') {
    const q = Math.floor(d.getMonth() / 3) + 1;
    return `Q${q} '${String(d.getFullYear()).slice(-2)}`;
  }
  if (granularity === 'month') {
    return d.toLocaleDateString(undefined, { month: 'short' });
  }
  // week / day
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

// Generate tick dates at appropriate intervals for the axis span.
// Skips ticks too close to the endpoints (within 4% of total span).
function generateTicks(axisStart, axisEnd) {
  if (!axisStart || !axisEnd) return [];
  const spanMs = axisEnd.getTime() - axisStart.getTime();
  const spanDays = spanMs / MS_PER_DAY;
  const edgePadMs = spanMs * 0.04;
  const cutStart = axisStart.getTime() + edgePadMs;
  const cutEnd = axisEnd.getTime() - edgePadMs;

  let granularity;
  let stepFn;
  if (spanDays > 730) {
    granularity = 'year';
    stepFn = (d) => new Date(d.getFullYear() + 1, 0, 1);
  } else if (spanDays > 365) {
    granularity = 'quarter';
    stepFn = (d) => {
      const m = d.getMonth();
      const nextQuarterMonth = m - (m % 3) + 3;
      return new Date(d.getFullYear(), nextQuarterMonth, 1);
    };
  } else if (spanDays > 60) {
    granularity = 'month';
    stepFn = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 1);
  } else if (spanDays > 14) {
    granularity = 'week';
    stepFn = (d) => new Date(d.getTime() + 7 * MS_PER_DAY);
  } else {
    granularity = 'day';
    stepFn = (d) => new Date(d.getTime() + MS_PER_DAY);
  }

  const ticks = [];
  // Start from the next boundary after axisStart
  let cursor;
  if (granularity === 'year') {
    cursor = new Date(axisStart.getFullYear() + 1, 0, 1);
  } else if (granularity === 'quarter') {
    const m = axisStart.getMonth();
    const nextQuarterMonth = m - (m % 3) + 3;
    cursor = new Date(axisStart.getFullYear(), nextQuarterMonth, 1);
  } else if (granularity === 'month') {
    cursor = new Date(axisStart.getFullYear(), axisStart.getMonth() + 1, 1);
  } else if (granularity === 'week') {
    // Next Monday after axisStart
    const day = axisStart.getDay();
    const offsetToMon = (8 - day) % 7 || 7;
    cursor = new Date(axisStart.getTime() + offsetToMon * MS_PER_DAY);
  } else {
    cursor = new Date(axisStart.getTime() + MS_PER_DAY);
  }

  let safety = 200; // hard cap on tick count
  while (cursor.getTime() < axisEnd.getTime() && safety > 0) {
    if (cursor.getTime() >= cutStart && cursor.getTime() <= cutEnd) {
      ticks.push(cursor);
    }
    cursor = stepFn(cursor);
    safety -= 1;
  }
  return ticks.map((d) => ({ date: d, granularity }));
}

function TopicTimeline({ transaction_summaries = [], key_dates = [], onSelectDate }) {
  const { axisStart, axisEnd, txnPoints, kdPoints, ticks } = useMemo(() => {
    const txnPts = (transaction_summaries || [])
      .map((t) => {
        const d = parseDate(t.date);
        return d ? { date: d, amount: t.amount, id: t.id } : null;
      })
      .filter(Boolean);

    const kdParsed = (key_dates || [])
      .map((kd) => {
        const start = parseDate(kd.start_date);
        const end = parseDate(kd.end_date) || start;
        return start ? { ...kd, start, end } : null;
      })
      .filter(Boolean);

    const allDates = [
      ...txnPts.map((t) => t.date),
      ...kdParsed.map((kd) => kd.start),
      ...kdParsed.map((kd) => kd.end),
    ].filter(Boolean);

    if (allDates.length === 0) {
      return { axisStart: null, axisEnd: null, txnPoints: [], kdPoints: kdParsed, ticks: [] };
    }

    const minMs = Math.min(...allDates.map((d) => d.getTime()));
    const maxMs = Math.max(...allDates.map((d) => d.getTime()));
    // 30-day padding on either side so endpoints don't sit on the edge.
    // For a single-point topic, this also gives the dot some room to breathe.
    const padMs = 30 * MS_PER_DAY;
    const aStart = new Date(minMs - padMs);
    const aEnd = new Date(maxMs + padMs);

    return {
      axisStart: aStart,
      axisEnd: aEnd,
      txnPoints: txnPts,
      kdPoints: kdParsed,
      ticks: generateTicks(aStart, aEnd),
    };
  }, [transaction_summaries, key_dates]);

  if (!axisStart || !axisEnd) {
    return null;
  }

  const totalSpanMs = axisEnd.getTime() - axisStart.getTime();

  function xPct(date) {
    if (!date) return 0;
    return ((date.getTime() - axisStart.getTime()) / totalSpanMs) * 100;
  }

  // Bin transactions by ~1.2% width so dense topics are still legible.
  // Each bin tracks positive and negative sums (and per-side counts)
  // separately so income and expenses on the same day render as two
  // bars rather than collapsing to a single net value.
  const binSizePct = 1.2;
  const bins = new Map();
  for (const t of txnPoints) {
    const x = xPct(t.date);
    const binKey = Math.round(x / binSizePct);
    const bin = bins.get(binKey) || {
      xPct: binKey * binSizePct,
      count: 0,
      sumAbs: 0,
      sumSigned: 0,
      sumPositive: 0,
      sumNegative: 0,
      countPositive: 0,
      countNegative: 0,
      firstDate: t.date,
      lastDate: t.date,
      amounts: [],
    };
    bin.count += 1;
    if (typeof t.amount === 'number') {
      bin.sumAbs += Math.abs(t.amount);
      bin.sumSigned += t.amount;
      if (t.amount > 0) {
        bin.sumPositive += t.amount;
        bin.countPositive += 1;
      } else if (t.amount < 0) {
        bin.sumNegative += t.amount;
        bin.countNegative += 1;
      }
    }
    if (t.date.getTime() < bin.firstDate.getTime()) bin.firstDate = t.date;
    if (t.date.getTime() > bin.lastDate.getTime()) bin.lastDate = t.date;
    if (bin.amounts.length < 5) bin.amounts.push(t.amount);
    bins.set(binKey, bin);
  }
  const dots = Array.from(bins.values());
  const sparse = dots.length > 0 && dots.length <= SPARSE_DOT_THRESHOLD;

  // Mid-line y in the SVG viewBox (also matches CSS px since viewBox height
  // and CSS plot height are 1:1 on the Y axis).
  const midY = PADDING_TOP + (HEIGHT - PADDING_TOP - PADDING_BOTTOM) / 2;

  // Bar sizing: normalize against the largest single-side magnitude across
  // all visible bins so that positives and negatives share one comparable
  // scale (a $300 income bar is visually taller than an $80 expense bar
  // regardless of net direction). Bins whose amounts are missing still
  // render a single fallback indicator so the date isn't invisible.
  const MAX_BAR_PX = 36;
  const MIN_BAR_PX = 5;
  const FALLBACK_BAR_PX = 8;
  const maxSideMagnitude = dots.reduce((m, b) => {
    const pos = Number.isFinite(b.sumPositive) ? b.sumPositive : 0;
    const neg = Number.isFinite(b.sumNegative) ? Math.abs(b.sumNegative) : 0;
    return Math.max(m, pos, neg);
  }, 0);

  function valueHeightPx(magnitude) {
    if (!Number.isFinite(magnitude) || magnitude <= 0 || maxSideMagnitude === 0) {
      return 0;
    }
    const ratio = magnitude / maxSideMagnitude;
    return Math.max(MIN_BAR_PX, Math.round(ratio * MAX_BAR_PX));
  }

  // Match NetWorthChart's rotated-label headroom: vertical projection
  // of the longest label at -45° plus 8px breathing room, floored at
  // KEY_DATE_LANE_MIN_PX so a single short label still has visible space.
  const longestLabelChars = kdPoints.reduce(
    (m, kd) => Math.max(m, (kd.label || '').length),
    0
  );
  const longestLabelPx = longestLabelChars * KEY_DATE_FONT_PX * 0.55;
  const keyDateLaneHeight = kdPoints.length
    ? Math.max(KEY_DATE_LANE_MIN_PX, Math.ceil(longestLabelPx * Math.SQRT1_2) + 8)
    : 0;

  function binDateLabel(bin) {
    return bin.firstDate.getTime() === bin.lastDate.getTime()
      ? formatDateLabel(bin.firstDate)
      : `${formatDateLabel(bin.firstDate)} – ${formatDateLabel(bin.lastDate)}`;
  }

  function buildSideTooltip(bin, side) {
    const sum = side === 'positive' ? bin.sumPositive : bin.sumNegative;
    const count = side === 'positive' ? bin.countPositive : bin.countNegative;
    const noun = count === 1 ? 'transaction' : 'transactions';
    return `${binDateLabel(bin)} · ${count} ${noun} · $${formatCurrency(sum)}`;
  }

  function buildFallbackTooltip(bin) {
    const noun = bin.count === 1 ? 'transaction' : 'transactions';
    return `${binDateLabel(bin)} · ${bin.count} ${noun}`;
  }

  function inlineLabel(bin) {
    const compactDate = bin.firstDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    if (bin.count === 1 && bin.sumAbs > 0) {
      return `${compactDate} · $${formatCurrency(bin.sumSigned)}`;
    }
    if (bin.sumPositive > 0 && bin.sumNegative < 0) {
      return `${compactDate} · +$${formatCurrency(bin.sumPositive)} / -$${formatCurrency(Math.abs(bin.sumNegative))}`;
    }
    if (bin.sumPositive > 0) {
      return `${compactDate} · +$${formatCurrency(bin.sumPositive)}`;
    }
    if (bin.sumNegative < 0) {
      return `${compactDate} · -$${formatCurrency(Math.abs(bin.sumNegative))}`;
    }
    return compactDate;
  }

  return (
    <div className={styles.wrapper} role="img" aria-label="Topic timeline">
      <div className={styles.endpoints}>
        <span>{formatDateLabel(axisStart)}</span>
        <span>{formatDateLabel(axisEnd)}</span>
      </div>

      {/* Key-date label lane — matches NetWorthChart: -45° rotated labels
          anchored at the top of each marker line, colored by confidence.
          DOM (not SVG) because the plot's SVG uses preserveAspectRatio="none"
          which would distort rotated text. */}
      {kdPoints.length > 0 ? (
        <div
          className={styles.keyDateLabelLayer}
          style={{ height: `${keyDateLaneHeight}px` }}
        >
          {kdPoints.map((kd) => {
            const startX = xPct(kd.start);
            const endX = kd.end ? xPct(kd.end) : null;
            const isSpan =
              kd.kind === 'span' && endX != null && endX !== startX;
            const labelX = isSpan ? (startX + endX) / 2 : startX;
            const color = CONFIDENCE_COLOR_VAR[kd.confidence] || CONFIDENCE_COLOR_VAR.asserted;
            return (
              <div
                key={`kdlabel-${kd.dateID}`}
                className={styles.keyDateLabel}
                style={{ left: `${labelX}%`, color }}
                title={`${kd.label} — ${formatDateLabel(kd.start)}${kd.confidence ? ` (${kd.confidence})` : ''}`}
              >
                {kd.label}
              </div>
            );
          })}
        </div>
      ) : null}

      <div className={styles.plot}>
        <svg
          viewBox={`0 0 100 ${HEIGHT}`}
          preserveAspectRatio="none"
          className={styles.svg}
          aria-hidden="true"
        >
          {/* Span key-date bands — drawn first, behind everything */}
          {kdPoints
            .filter((kd) => kd.kind === 'span' && kd.start && kd.end && kd.end.getTime() !== kd.start.getTime())
            .map((kd) => {
              const x1 = xPct(kd.start);
              const x2 = xPct(kd.end);
              const fill = CONFIDENCE_COLOR_VAR[kd.confidence] || CONFIDENCE_COLOR_VAR.asserted;
              return (
                <rect
                  key={`band-${kd.dateID}`}
                  x={x1}
                  y={PADDING_TOP - 4}
                  width={Math.max(x2 - x1, 0.4)}
                  height={HEIGHT - PADDING_TOP - PADDING_BOTTOM + 8}
                  fill={fill}
                  fillOpacity="0.12"
                />
              );
            })}

          {/* Mid-line axis */}
          <line
            x1={0}
            y1={midY}
            x2={100}
            y2={midY}
            stroke="var(--color-navy-300)"
            strokeWidth="0.18"
            vectorEffect="non-scaling-stroke"
          />

          {/* Point key-date markers — vertical lines */}
          {kdPoints
            .filter((kd) => !(kd.kind === 'span' && kd.end && kd.end.getTime() !== kd.start.getTime()))
            .map((kd) => {
              const x = xPct(kd.start);
              const stroke = CONFIDENCE_COLOR_VAR[kd.confidence] || CONFIDENCE_COLOR_VAR.asserted;
              return (
                <line
                  key={`marker-${kd.dateID}`}
                  x1={x}
                  y1={PADDING_TOP - 4}
                  x2={x}
                  y2={HEIGHT - PADDING_BOTTOM + 4}
                  stroke={stroke}
                  strokeWidth="0.4"
                  vectorEffect="non-scaling-stroke"
                  onClick={onSelectDate ? () => onSelectDate(kd.dateID) : undefined}
                  style={onSelectDate ? { cursor: 'pointer' } : undefined}
                >
                  <title>{`${kd.label} — ${formatDateLabel(kd.start)}${kd.confidence ? ` (${kd.confidence})` : ''}`}</title>
                </line>
              );
            })}
        </svg>

        {/* X-axis tick layer — DOM, sized in px so it survives the SVG stretch.
            Suppressed in sparse mode because the per-dot inline labels
            already carry the date and would visually collide with ticks. */}
        {!sparse ? (
          <div className={styles.tickLayer} aria-hidden="true">
            {ticks.map((tick, idx) => (
              <div
                key={`tick-${idx}`}
                className={styles.tick}
                style={{ left: `${xPct(tick.date)}%` }}
              >
                <div className={styles.tickLine} />
                <div className={styles.tickLabel}>{formatTickLabel(tick.date, tick.granularity)}</div>
              </div>
            ))}
          </div>
        ) : null}

        {/* Transaction bar layer — DOM, fixed pixel size (SVG shapes get
            distorted by preserveAspectRatio="none"). Each bin emits up
            to two bars: an upward green bar for the sum of positive
            amounts and a downward red bar for the sum of negative
            amounts, both anchored at the midline. Heights share one
            normalization scale so visual comparison is meaningful. */}
        <div className={styles.barLayer}>
          {dots.map((bin, idx) => {
            const upHeight = valueHeightPx(bin.sumPositive);
            const downHeight = valueHeightPx(Math.abs(bin.sumNegative));
            const hasUp = upHeight > 0;
            const hasDown = downHeight > 0;
            // No usable amount on either side — render a single neutral
            // indicator so the date itself doesn't disappear from the chart.
            if (!hasUp && !hasDown) {
              return (
                <div
                  key={`bar-${idx}`}
                  className={`${styles.bar} ${styles.barUp}`}
                  style={{
                    left: `${bin.xPct}%`,
                    height: `${FALLBACK_BAR_PX}px`,
                    top: `${midY - FALLBACK_BAR_PX}px`,
                  }}
                  title={buildFallbackTooltip(bin)}
                />
              );
            }
            return (
              <React.Fragment key={`bar-${idx}`}>
                {hasUp ? (
                  <div
                    className={`${styles.bar} ${styles.barUp}`}
                    style={{
                      left: `${bin.xPct}%`,
                      height: `${upHeight}px`,
                      top: `${midY - upHeight}px`,
                    }}
                    title={buildSideTooltip(bin, 'positive')}
                  >
                    {bin.countPositive > 1 ? (
                      <span className={styles.barCount}>{bin.countPositive}</span>
                    ) : null}
                  </div>
                ) : null}
                {hasDown ? (
                  <div
                    className={`${styles.bar} ${styles.barDown}`}
                    style={{
                      left: `${bin.xPct}%`,
                      height: `${downHeight}px`,
                      top: `${midY}px`,
                    }}
                    title={buildSideTooltip(bin, 'negative')}
                  >
                    {bin.countNegative > 1 ? (
                      <span className={styles.barCount}>{bin.countNegative}</span>
                    ) : null}
                  </div>
                ) : null}
              </React.Fragment>
            );
          })}
        </div>

        {/* Sparse-mode inline labels — only when the topic has very few dots,
            because at scale the labels overlap and become noise. Placed under
            the dot to avoid colliding with the keydate label row above. */}
        {sparse ? (
          <div className={styles.inlineLabelLayer} aria-hidden="true">
            {dots.map((bin, idx) => (
              <div
                key={`ilabel-${idx}`}
                className={styles.inlineLabel}
                style={{ left: `${bin.xPct}%` }}
              >
                {inlineLabel(bin)}
              </div>
            ))}
          </div>
        ) : null}
      </div>

    </div>
  );
}

export default TopicTimeline;
